import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PatternLock from "@/components/PatternLock";

/**
 * AppLockGate — a full-screen overlay rendered when the logged-in user has
 * `app_lock_enabled=true`. Blocks access to the app until they draw the
 * correct pattern. Once unlocked it stays unlocked for this browser session
 * (sessionStorage flag) so navigating between dashboard / vault / etc. does
 * NOT re-prompt — only fresh tab-opens / page reloads do.
 */
const SESSION_KEY = "mmh_app_unlocked_at";

export default function AppLockGate({ children }) {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    // If the user just logged in OR the session flag is set, skip lock
    if (window.sessionStorage.getItem(SESSION_KEY)) {
      setUnlocked(true);
    }
  }, []);

  // No user, user not loaded yet, or app lock disabled → render children
  if (!user || user === false || !user.app_lock_enabled || unlocked) {
    return children;
  }

  const onPattern = async (pattern) => {
    setError(false);
    try {
      await api.post("/account/app-lock/verify", { pattern });
      window.sessionStorage.setItem(SESSION_KEY, String(Date.now()));
      setUnlocked(true);
    } catch {
      setError(true);
    }
  };

  return (
    <div
      className="hero-theme min-h-screen flex flex-col items-center justify-center px-5"
      data-testid="app-lock-gate"
    >
      <div className="flex items-center gap-2 mb-6">
        <Lock className="w-5 h-5 text-[#F5D849]" strokeWidth={2.6} />
        <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#F5D849]">My Money Hero</span>
      </div>
      <h1
        className="text-2xl font-black text-white text-center uppercase tracking-wider"
        style={{ fontFamily: "Outfit, sans-serif" }}
      >
        Welcome back, hero.
      </h1>
      <p className="mt-2 text-sm text-[#C7DAD3] text-center">
        Draw your unlock pattern to enter.
      </p>
      <div className="mt-8">
        <PatternLock size={280} onComplete={onPattern} error={error} />
      </div>
      {error && (
        <p className="mt-4 text-sm font-extrabold text-[#FF3B3B] uppercase tracking-wider" data-testid="app-lock-error">
          Wrong pattern — try again
        </p>
      )}
    </div>
  );
}
